import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';
import { Terms } from './AccountManagerPlans';

/**
 * System Admin: the reseller terms account managers see under Plans & Pricing (add-ons, discounts, margin, support). Shown as the account managers see it; Edit opens the
 * text box, and Save or Cancel goes back to reading.
 */
function PartnerPriceListTermsEditor() {
  const [content, setContent] = useState('');
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    axios.get('/api/system-admin/partner-price-list-terms')
      .then((r) => { setContent(r.data.content || ''); setSaved(r.data); })
      .catch(() => setProblem('Could not load the terms'));
  }, []);

  const startEditing = () => { setDraft(content); setProblem(''); setEditing(true); };
  const cancel = () => { setEditing(false); setProblem(''); };

  const save = async () => {
    setSaving(true);
    setProblem('');
    try {
      const r = await axios.put('/api/system-admin/partner-price-list-terms', { content: draft });
      setContent(r.data.content || '');
      setSaved(r.data);
      setEditing(false);
      toast.success('Reseller terms saved');
    } catch (e) {
      const data = e.response?.data;
      setProblem(typeof data === 'string' ? data : 'Could not save the terms');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-4" data-testid="reseller-terms">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Reseller price list terms</h5>
        {!editing && <Button size="sm" variant="outline-primary" onClick={startEditing} data-testid="edit-reseller-terms"><i className="bi bi-pencil me-1" />Edit</Button>}
      </Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Shown to account managers under Account Management &gt; Plans &amp; Pricing, below the plans: add-ons, discounts,
          margin, support and renewal terms. Plans, prices and features come from the plans above and are not repeated here.
        </p>
        {problem && <Alert variant="danger" className="py-2">{problem}</Alert>}
        {editing ? (
          <>
            <p className="text-muted small">Start a line with <code>## </code> for a heading and <code>- </code> for a bullet.</p>
            <Form.Group controlId="partner-terms-content">
              <Form.Control as="textarea" rows={18} value={draft} onChange={(e) => setDraft(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} autoFocus />
            </Form.Group>
            <div className="d-flex align-items-center gap-2 mt-3">
              <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save terms'}</Button>
              <Button variant="outline-secondary" onClick={cancel} disabled={saving}>Cancel</Button>
            </div>
          </>
        ) : (
          <>
            {content.trim() ? <Terms text={content} /> : <p className="text-muted">No terms written yet. Press Edit to add them.</p>}
            {saved?.updatedAt && <div className="text-muted small mt-3">Last saved {formatDateTime(saved.updatedAt)}{saved.updatedByEmail ? ` by ${saved.updatedByEmail}` : ''}</div>}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default PartnerPriceListTermsEditor;
